import React from 'react';
import FramePage from 'components/framePage/FramePage';
import Error404 from 'screens/errors/404/Error404';
import { getItem } from 'helpers/storage';
import { getUserData } from 'helpers/user/getUserData';
import { getUserAccountType } from 'helpers/user/getUserAccountType';
import { isUserManagementAllowed } from 'helpers/user/isUserManagementAllowed';
import { useTranslation } from 'react-i18next';
import { AccessType } from '../enums/AccessType';
import { MicroAppEmbed } from './MicroAppEmbed';
import { getFlag } from 'helpers/featureFlags/featureFlags';

const GLOBAL_NAME = 'SelfAssessmentTrends';
const BUNDLE_FILE_NAME = 'self-assessment-trends.js';
const MOUNT_BASENAME = '/self-assessments/new-trends';
const MISSING_BUNDLE_URL_MESSAGE =
    'MICRO_APP_SELF_ASSESSMENT_TRENDS_URL is not configured';

/**
 * Origin from env + IIFE filename. Returns null when unset so we never resolve
 * to a relative `/self-assessment-trends.js` on the admin origin.
 */
function resolveBundleUrl(rawUrl: unknown): string | null {
    const origin = String(rawUrl ?? '').trim();
    if (!origin) {
        return null;
    }
    return `${origin.replace(/\/?$/, '/')}${BUNDLE_FILE_NAME}`;
}

// Client/hotel user types allowed to reach the page. Mirrors the `anyAdminUser`
// `allowedUserTypes` the nav uses for the existing `/self-assessments/trends`
// page (see getMenuItems.ts).
const ALLOWED_ACCESS_TYPES: AccessType[] = [
    AccessType.brandClientUser,
    AccessType.clientUser,
    AccessType.masterClientUser,
    AccessType.regionalClientUser,
    AccessType.hotelUser,
];

// Access rule for the Self Assessment Trends page — identical to the existing
// `/self-assessments/trends` page: any *admin* client/hotel user, where "admin"
// is `isUserManagementAllowed` (the flag the menu passes as `isUserAdmin`).
function isUserAllowedToSelfAssessmentTrends(userData): boolean {
    if (!userData) {
        return false;
    }
    const accountType = getUserAccountType(userData) as AccessType;
    return (
        Boolean(isUserManagementAllowed(userData)) &&
        ALLOWED_ACCESS_TYPES.includes(accountType)
    );
}

function SelfAssessmentTrendsRemote() {
    const { t } = useTranslation();
    const title = t('SELF_ASSESSMENT.TRENDS.TITLE');
    const bundleUrl = resolveBundleUrl(Env.MICRO_APP_SELF_ASSESSMENT_TRENDS_URL);

    if (!bundleUrl) {
        return (
            <FramePage title={title}>
                <div style={{ padding: 24, color: '#b00020' }}>
                    {MISSING_BUNDLE_URL_MESSAGE}
                </div>
            </FramePage>
        );
    }

    return (
        <FramePage title={title}>
            <MicroAppEmbed
                bundleUrl={bundleUrl}
                globalName={GLOBAL_NAME}
                basename={MOUNT_BASENAME}
            />
        </FramePage>
    );
}

// Route entry point. Restricts the page to the same users as the existing
// `/self-assessments/trends` page; unauthorized users get the 404 page and the
// remote bundle is never loaded for them.
export function SelfAssessmentTrends() {
    const isAllowed =
        getFlag('isNewSelfAssessmentTrendsEnabled') &&
        Boolean(getItem('user')) &&
        isUserAllowedToSelfAssessmentTrends(getUserData(false));

    if (!isAllowed) {
        return <Error404 />;
    }

    return <SelfAssessmentTrendsRemote />;
}
